import * as React from "react";
import {
    Employee,
    EmployeeList,
    GetEmployeesWithActivePeriodQuery,
    GetWorkingEventListRequest,
    JsonPatchOperation,
    MatchEmployeeRequest,
    MatchEmployeeResponse,
    MergeRelativeRequest,
    MergeRelativeResponse,
    PatchRelativeResponse,
    WorkingEventList,
} from "@skbkontur/staff-common";
import { EmployeeListInitialState } from "@skbkontur/staff-selector-widget";

import { SectionName, UnitId } from "../../Controls/FormParts/Unit/UnitId/UnitId";
import { FormResources, ReloadableFormResources } from "../../Forms/FormResources";
import { IPublicForm } from "../../Forms/PublicForm/IPublicForm";
import { FormSettings } from "../../Forms/Types/FormSettings";
import {
    GetUserResponse,
    ICandyApiClient,
    ICandyApiDraftClient,
} from "../../../../Common/CandyApiClient/ICandyApiClient";
import { ITemplatesApiClient } from "../Api/TemplateApiClient/ITemplatesApiClient";
import { ICalculator2 } from "../Core/Calculator2/Calculator2";
import { IChangeSetManager } from "../Core/ChangeSet/IChangeSetManager";
import { BindingPath } from "../Core/CoreTypes";
import { DataDeclarationHelper } from "../Core/DataDeclaration/DataDeclarationHelper";
import { IErrorManager } from "../Core/ErrorManager/ErrorManager";
import { ExternalEvent, ExternalEventPath } from "../Core/EventManagement/ExternalEvents/ExternalEvent";
import { IExternalEventEmitter } from "../Core/EventManagement/ExternalEvents/ExternalEventManager";
import { ExternalHookFunction } from "../Core/EventManagement/ExternalEvents/ExternalHookFunction";
import { Localization } from "../Core/Localization/Localization";
import { IReadonlyModelHunk } from "../Core/ModelStoring/IReadonlyModelHunk";
import { Sections } from "../Core/ModelStoring/Sections";
import { NavigationPathMapper } from "../Core/PathUtils/NavigationPathMapper";
import { IPicklistsProvider } from "../Core/Picklists/IPicklistsProvider";
import { FormStore } from "../Core/Store/CandyStore";
import { IValidator } from "../Core/Validator/Validator";
import { IAttachmentsManager } from "../Externals/Attachments/IAttachmentsManager";
import { IFocusProvider } from "../Externals/FocusProvider/IFocusProvider";
import { IControlRegistry } from "../Core/ComponentHandleRegistry/IControlRegistry";
import { IActiveLogHelper } from "../Externals/ActiveLog/IActiveLogHelper";
import { IUnitNavigator } from "../../Controls/FormParts/Navigation/IUnitNavigator";
import { IFufManager } from "../Externals/FufManager/IFufManager";
import { ModelEventsListener } from "../Core/EventManagement/ModelEvents/ModelEvent";
import { ITestLogger } from "../Testing/TestLogging/ITestLogger";
import { FeatureFlags } from "../../Forms/Types/FeatureFlags";
import { INidusClient } from "../../../../Common/NidusClient/INidusClient";
import { ILazyLoadingManager } from "../Core/LazyLoading/LazyLoadingManager";
import { CandyInjectableModules } from "../../Forms/CandyInjectableModules";
import { ReloadableRawFormResources } from "../../Forms/RawFormResources";
import { ModelChangeListener } from "../../Forms/PublicForm/ModelChangeListener";
import { CustomNormalizationHelper } from "../Core/Normalization/CustomNormalizationHelper";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../Common/ModelPath/ModelPath";
import { IFormStaffClient } from "../../StaffServiceIntegration/FormStaffClient";
import { MassActionParameters } from "../../../../Common/CandyApiClient/Types/MassActions/MassActionParameters";
import { ImportEmployeesOptions } from "../../../../Common/CandyApiClient/Types/Staff/ImportEmployeesOptions";
import { ModalOptions } from "../Core/ModalManager/IModalState";
import { ClearPageMethod } from "../ActionCreators/UnitActionCreators";
import { IErrorLogApi } from "../Core/ErrorManager/ErrorLogApi";
import { IFSPrinterClient } from "../../../../Common/FSPrinterClient/IFSPrinterClient";
import {
    EmployerNormalizedTag,
    ImportEmployerDocumentsOptions,
    ImportFromEmployeeServiceOptions,
} from "../../StaffServiceIntegration/EmployeeServiceTypes";
import { FilteringManager } from "../Core/Filtering/FilteringManager";
import { ValueForInstancesSettingRule } from "../../../../Common/CandyApiClient/Types/MassActions/ValueForInstancesSettingRule";
import { INdsPlusApiClient } from "../../../../Common/NdsPlusApiClient/INdsPlusApiClient";
import { IContractorsApiClient } from "../../ContractorsServiceIntegration/ContractorsApiClient";
import {
    GetContractorResponse,
    ImportContractorsWidgetOptions,
} from "../../ContractorsServiceIntegration/Types/ContractorsTypes";
import { HighlightedControl, HighlightedControlWithFocus } from "../../Controls/ExternalHighlighting";
import { TrackCallback } from "../../Controls/Internals/Metrics/MetricsProviderContext";

import { IHelperFunctionsCallContextProvider } from "./CustomizationContexts/HelperFunctionContext/IHelperFunctionsCallContext";
import {
    IFormHookCallContext,
    IFormHookCallContextProvider,
} from "./CustomizationContexts/HooksContext/IFormHookCallContext";
import { IFormModelAccessor } from "./IFormModelAccessor";
import { ModelChangeHooks, Subscription } from "./ModelChangeEventListener";

export interface RequisitesInfo {
    [key: string]: { value: undefined | null | string };
}

export interface FormHooks {
    readonly beforeInit: (form: IFormHookCallContext) => void;
    readonly afterInit: (form: IFormHookCallContext) => void | Promise<void>;
    readonly beforeUnload: (form: IFormHookCallContext) => void;
}

export interface ITrackActionProvider {
    track: TrackCallback;
}

export interface IModelChangeEventListener {
    onBeforeModelChange(path: string | string[], listener: ModelChangeListener): Subscription;
    onModelChange(path: string | string[], listener: ModelChangeListener): Subscription;
}

export enum CalculationAwaitingOption {
    Calculations,
    CalculationsAndValidations,
}

export type DependencyPathsCallback = (paths: AbsoluteModelPath[]) => void;
export type HighlightedPathsCallback = (paths: HighlightedControlWithFocus[]) => void;

export interface IForm
    extends IHelperFunctionsCallContextProvider,
        IFormHookCallContextProvider,
        IExternalEventEmitter,
        IFormModelAccessor,
        IModelChangeEventListener,
        ITrackActionProvider {
    readonly calculator2: ICalculator2;
    readonly changeSet: IChangeSetManager;
    readonly publicForm: IPublicForm;
    readonly activeLogHelper: IActiveLogHelper;
    readonly dataDeclarationHelper: DataDeclarationHelper;
    readonly errorManager: IErrorManager;
    readonly errorLogApi: IErrorLogApi;
    readonly focusProvider: IFocusProvider;
    readonly fufManager: IFufManager;
    readonly hooks: FormHooks;
    readonly navigationPathMapper: NavigationPathMapper;
    readonly picklistsProvider: IPicklistsProvider;
    readonly sections: Sections;
    readonly settings: FormSettings;
    readonly validator: IValidator;
    readonly localization: Localization;
    readonly candyApi: ICandyApiClient;
    readonly candyApiDraftClient: ICandyApiDraftClient;
    readonly staffClient: IFormStaffClient;
    readonly contractorsClient: IContractorsApiClient;
    readonly fsPrinterClient: IFSPrinterClient;
    readonly templatesApi: ITemplatesApiClient;
    readonly componentHandleRegistry: IControlRegistry;
    readonly testLogger: ITestLogger;
    readonly requisiteList: string[];
    readonly nidusClient: INidusClient;
    readonly store: FormStore;
    readonly lazyLoadingManager: ILazyLoadingManager;
    readonly customNormalizationHelper: CustomNormalizationHelper;
    readonly filteringManager: FilteringManager;
    readonly attachmentsPublicForms: { [attachmentId: string]: IPublicForm };
    readonly ndsPlusApiClient: INdsPlusApiClient;
    readonly track: TrackCallback;

    getModelChangeEventListenerHooks(): ModelChangeHooks;
    getBeforeModelChangeEventListenerHooks(): ModelChangeHooks;
    featureFlags: FeatureFlags;
    requisites: RequisitesInfo;
    ieSizeError: boolean;
    attachments: IAttachmentsManager;
    unmount: () => void;
    unitNavigator?: IUnitNavigator;
    updateFormResources?: (
        FormCreator: (allModules: CandyInjectableModules) => React.ComponentType,
        resources: ReloadableRawFormResources
    ) => void;

    replaceResources(prevResources: FormResources, resources: ReloadableFormResources): Promise<void>;

    triggerExternalEvent(event: ExternalEvent): void;
    triggerExternalEvents(events: ExternalEvent[]): void;
    hasExternalEventSubscriber(pathArr: ExternalEventPath): boolean;
    offExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): void;
    onExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): void;
    onceExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): ExternalHookFunction;

    disableAllValidations(): void;
    enableAllValidations(): void;
    revalidatePaths(paths: Iterable<AbsoluteModelPath>): Promise<void>;
    recalculateTargets(paths: readonly string[]): Promise<void>;
    runCheckFromPublicForm(): Promise<void>;
    setErrorLogForTestsOnly(errorLog: ServerErrorLog): void;
    renormalizePage(sectionName: SectionName, bindingPath: AbsoluteResolvedModelPath): Promise<void>;
    clearSection(
        sectionName: SectionName,
        bindingPath: AbsoluteResolvedModelPath | undefined,
        method: ClearPageMethod
    ): Promise<void>;
    normalizeByPrefix(nodePathPrefix: AbsoluteResolvedModelPath): void;
    normalizeSection(sectionName: SectionName): Promise<void>;
    removeSection(unitType: SectionName, bindingPath?: AbsoluteResolvedModelPath): void;

    loadDataIntoForm(data: IReadonlyModelHunk): Promise<void>;
    performInitialFormLoad(data: IReadonlyModelHunk, settings: FormSettings): Promise<void>;
    performFormLoad(data: IReadonlyModelHunk): Promise<void>;
    performFormLazyLoad(data: IReadonlyModelHunk): void;
    reloadInner(): Promise<void>;

    getChildrenPaths(path: AbsoluteModelPath): Iterable<AbsoluteResolvedModelPath>;
    getDependsOnPaths(path: AbsoluteModelPath): AbsoluteModelPath[];
    resolveMask(pathWithMask: BindingPath): BindingPath[];
    openModalUnit(targetUnit: UnitId, modalUnit: UnitId, withScrollToUnit?: boolean): void;
    closeModalUnit(targetUnit: UnitId): void;
    showCustomModal(options: ModalOptions): void;
    waitForPendingCalculations(calculationAwaitingOption?: CalculationAwaitingOption): Promise<void>;

    onErrorsCountChanged(listener: ModelEventsListener): void;
    offErrorsCountChanged(listener: ModelEventsListener): void;

    setHoveredPath(path: AbsoluteResolvedModelPath): void;
    onDependencyPathsFromHoveredPathChanged(callback: DependencyPathsCallback): () => void;

    setExternalHighlightedPaths(paths: Array<HighlightedControl<AbsoluteModelPath>>): void;
    onExternalHighlightPaths(callback: HighlightedPathsCallback): () => void;
    focusOnHighlightedControl(path: AbsoluteModelPath | undefined): void;

    focusFirstInvalidControl(): Promise<boolean>;
    focusFirstInvalidControlByPath(validationPath: string): boolean;

    getWorkingEventsForEmployee(query: GetWorkingEventListRequest): Promise<WorkingEventList | undefined>;
    getAdditionalEmployeeInfo(query: GetEmployeesWithActivePeriodQuery): Promise<EmployeeList | undefined>;
    getEmployees(query: GetEmployeesWithActivePeriodQuery): Promise<EmployeeList>;
    importFromEmployeeService(
        options?: ImportFromEmployeeServiceOptions,
        initialState?: EmployeeListInitialState
    ): Promise<Employee[] | undefined>;
    importEmployerDocument(options: ImportEmployerDocumentsOptions): Promise<EmployerNormalizedTag | undefined>;

    importEmployees(employees: Employee[], options?: ImportEmployeesOptions): Promise<void>;
    mergeRelative(
        bindingPath: BindingPath,
        employeeId: string,
        query: MergeRelativeRequest
    ): Promise<MergeRelativeResponse | undefined>;
    patchRelative(
        employeeId: string,
        relativeId: string,
        operations: JsonPatchOperation[]
    ): Promise<PatchRelativeResponse>;
    matchEmployee(query: MatchEmployeeRequest): Promise<MatchEmployeeResponse>;

    importContractorsWidgetOptions(
        options: ImportContractorsWidgetOptions
    ): Promise<GetContractorResponse[] | undefined>;

    runMassAction(actionId: string, parameters?: MassActionParameters): Promise<void>;
    getSectionsByPath(bindingPath: AbsoluteResolvedModelPath, parentInstances: string[]): SectionName[];
    customNormalize(
        normalizerId: string | null,
        pathPrefixes: AbsoluteModelPath[],
        forceReload: boolean
    ): Promise<void>;
    normalizeDirtyMasksAndReloadInnerIfRequired(): Promise<void>;
    setMultipleWithNormalization(rules: ValueForInstancesSettingRule[]): Promise<void>;
    getAttachmentsPublicFormsById(id: string): IPublicForm;
    getAttachmentPath(id: string): string | undefined;
    getPortalUser(): Promise<GetUserResponse>;
}
